import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit2, Trash2, FileText, Eye, Calendar, Tag, Upload, X } from 'lucide-react';
import { blogAPI, uploadAPI } from '../../utils/api';
import { toast } from 'sonner';
import MDEditor from '@uiw/react-md-editor';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    image: '',
    tags: [],
    is_published: false
  });
  const [newTag, setNewTag] = useState('');
  const [uploading, setUploading] = useState(false);
  const [availableImages, setAvailableImages] = useState([]);
  const [showImagePicker, setShowImagePicker] = useState(false);

  useEffect(() => {
    fetchPosts();
    fetchAvailableImages();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await blogAPI.getAllAdmin();
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Ошибка загрузки статей');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableImages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/files`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      const data = await response.json();
      const images = data.files.filter(file => 
        file.filename.match(/\.(jpg|jpeg|png|gif|webp)$/i)
      );
      setAvailableImages(images);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      image: '',
      tags: [],
      is_published: false
    });
    setNewTag('');
    setEditingPost(null);
  };

  const openDialog = (post = null) => {
    if (post) {
      setFormData({
        title: post.title,
        content: post.content,
        excerpt: post.excerpt,
        image: post.image,
        tags: [...post.tags],
        is_published: post.is_published
      });
      setEditingPost(post);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadFile(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      setFormData(prev => ({
        ...prev,
        image: imageUrl
      }));
      
      // Refresh available images
      fetchAvailableImages();
      toast.success('Изображение успешно загружено');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Ошибка загрузки изображения');
    } finally {
      setUploading(false);
    }
  };

  const selectExistingImage = (imageUrl) => {
    setFormData(prev => ({
      ...prev,
      image: imageUrl
    }));
    setShowImagePicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPost) {
        await blogAPI.update(editingPost.id, formData);
        toast.success('Статья успешно обновлена');
      } else {
        await blogAPI.create(formData);
        toast.success('Статья успешно создана');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Ошибка сохранения статьи');
    }
  };

  const handleDelete = async (postId) => {
    if (confirm('Вы уверены, что хотите удалить эту статью?')) {
      try {
        await blogAPI.delete(postId);
        toast.success('Статья успешно удалена');
        fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
        toast.error('Ошибка удаления статьи');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление блогом</h1>
          <p className="text-gray-400">Создание и редактирование статей</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-post-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Новая статья
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPost ? 'Редактировать статью' : 'Создать новую статью'}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Заполните информацию о статье блога
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-gray-300">Заголовок статьи</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Введите заголовок статьи"
                  required
                  data-testid="post-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="excerpt" className="text-gray-300">Краткое описание</Label>
                <Textarea
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Краткое описание статьи (оставьте пустым для автогенерации)"
                  rows={3}
                  data-testid="post-excerpt-input"
                />
              </div>

              {/* Image Selection */}
              <div className="space-y-2">
                <Label className="text-gray-300">Изображение статьи</Label>
                
                {formData.image && (
                  <div className="relative mb-4">
                    <img 
                      src={formData.image} 
                      alt="Preview" 
                      className="max-w-xs h-32 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                      className="absolute top-1 right-1 w-6 h-6 p-0 bg-red-600 hover:bg-red-700"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Input
                    value={formData.image}
                    onChange={(e) => handleInputChange('image', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white flex-1 min-w-0"
                    placeholder="URL изображения"
                    data-testid="post-image-input"
                  />
                  
                  <Button
                    type="button"
                    onClick={() => setShowImagePicker(!showImagePicker)}
                    variant="outline"
                    className="border-gray-600"
                  >
                    Выбрать из загруженных
                  </Button>
                  
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('image-upload').click()}
                    disabled={uploading}
                    variant="outline"
                    className="border-gray-600"
                  >
                    <Upload className="mr-1 h-4 w-4" />
                    {uploading ? 'Загрузка...' : 'Загрузить новое'}
                  </Button>
                </div>

                {showImagePicker && availableImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 p-3 bg-gray-700 rounded-lg max-h-40 overflow-y-auto">
                    {availableImages.map((img) => (
                      <div
                        key={img.filename}
                        className="cursor-pointer hover:opacity-75 relative"
                        onClick={() => selectExistingImage(`${process.env.REACT_APP_BACKEND_URL}${img.url}`)}
                      >
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                          alt={img.filename}
                          className="w-full h-16 object-cover rounded"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Теги</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                    placeholder="Добавить тег"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    data-testid="add-tag-input"
                  />
                  <Button type="button" onClick={addTag} variant="outline" className="border-gray-600">
                    Добавить
                  </Button>
                </div>
                
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag, index) => (
                      <Badge
                        key={index}
                        className="bg-blue-600 text-white cursor-pointer"
                        onClick={() => removeTag(tag)}
                      >
                        {tag} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Содержание статьи</Label>
                <div className="bg-white rounded-lg p-2" data-color-mode="light">
                  <MDEditor
                    value={formData.content}
                    onChange={(value) => handleInputChange('content', value || '')}
                    preview="edit"
                    hideToolbar={false}
                    height={400}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_published"
                  checked={formData.is_published}
                  onCheckedChange={(checked) => handleInputChange('is_published', checked)}
                  data-testid="post-published-switch"
                />
                <Label htmlFor="is_published" className="text-gray-300">Опубликовать статью</Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-gray-600 text-gray-300"
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-post-btn"
                >
                  {editingPost ? 'Обновить' : 'Создать'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Posts List */}
      <div className="space-y-4">
        {posts.map((post, index) => (
          <motion.div
            key={post.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-xl font-semibold text-white">{post.title}</h3>
                      <Badge className={post.is_published ? 'bg-green-600' : 'bg-gray-600'}>
                        {post.is_published ? 'Опубликована' : 'Черновик'}
                      </Badge>
                    </div>
                    
                    {post.excerpt && (
                      <p className="text-gray-400 mb-3">{post.excerpt}</p>
                    )}
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(post.created_at)}
                      </div>
                      {post.tags.length > 0 && (
                        <div className="flex items-center">
                          <Tag className="h-4 w-4 mr-1" />
                          {post.tags.slice(0, 3).join(', ')}
                          {post.tags.length > 3 && '...'}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {post.image && (
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-24 h-16 object-cover rounded-lg ml-4"
                    />
                  )}
                </div>
                
                <div className="flex justify-end space-x-2 mt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(`/blog/${post.id}`, '_blank')}
                    className="text-gray-400 hover:text-white"
                    data-testid={`view-post-${post.id}`}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Просмотр
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog(post)}
                    className="text-blue-400 hover:text-blue-300"
                    data-testid={`edit-post-${post.id}`}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Редактировать
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(post.id)}
                    className="text-red-400 hover:text-red-300"
                    data-testid={`delete-post-${post.id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Удалить
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {posts.length === 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">Статьи пока не созданы</p>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Создать первую статью
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminBlog;